import React, { useState, useEffect, useContext } from 'react';
import FormInput from '../../common/FormInput';
import CarComponent from './CarComponent';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CommonValidation } from '../../common/commonValidation/commonValidation';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import { AlloyProcessStages } from './AlloyRepairRequestTypes';
import AlloySpecificForm from './AlloySpecificForm';
import './AlloyWheelSelection.scss';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import ModalDialog from '../../common/ModalDialog';
import ConfirmAlert from '../../common/confirmAlert/ConfirmAlert';
import { AlloyRepairRequestContext } from '../../../context/AlloyRepairRequestContext';
import { GoogleAnalytics } from '../../../classes/GoogleAnalytics';
import { Countries, PageNames } from '../../../Constants/Common';
import { TranslationHelper } from 'classes/TranslationHelper';
import { useTranslation } from 'react-i18next';
import { isAllowedInLocation } from '../../../helpers/location';
import { useAppSelector } from 'state/hooks';
import { selectSiteCountry } from 'state/selectors/site';

export type AlloyWheelSelectionProps = {
    cancelConfirmText: string;
};

const AlloyWheelSelection = ({
    cancelConfirmText
}: AlloyWheelSelectionProps): JSX.Element => {
    const [showDialog, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const siteLocation = useAppSelector(selectSiteCountry);
    const { t } = useTranslation();

    const {
        alloyRepairRequest,
        onWheelSelected,
        onUploadImageChange,
        onChangeDamageType,
        onFinishAddingWheels,
        onContinueToRepairAddress,
        onChangeStage,
        onWheelValueChange,
        onDeleteRepairRequest,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo
    } = useContext(AlloyRepairRequestContext);

    const {
        currentRepairRequest: repairRequest,
        alloyFormErrors,
        updateWheel,
        selectedWheels,
        showCancelAlert
    } = alloyRepairRequest;

    const { alloyWheelSpecifics: wheelFormErrors } = alloyFormErrors;

    useEffect(() => {
        if (CommonValidation.isNotEmpty(updateWheel)) {
            onWheelClick(updateWheel);
        }

        GoogleAnalytics.setPageView(PageNames.AlloyWheelSelection);
    }, []);

    const onShowConfirmation = (): void => {
        setShowConfirmation(true);
    };
    const onCloseConfirmation = (): void => {
        setShowConfirmation(false);
    };
    const onCancel = (): void => {
        setShowModal(false);
        setFormErrors('');
    };
    const onDeleteWheel = (): void => {
        setShowConfirmation(false);
        setShowModal(false);
        onDeleteRepairRequest(repairRequest?.damagedWheel || 'Unknown Wheel');
    };
    const onSave = (): void => {
        const isFormValid = onFinishAddingWheels();
        if (isFormValid === true) setShowModal(false);
    };

    const [formErrors, setFormErrors] = useState('');

    const onNextButtonClick = () => {
        if (selectedWheels && selectedWheels.length > 0) {
            onContinueToRepairAddress();
        } else {
            setFormErrors(t('select_wheel_for_repair'));
            GoogleAnalytics.trackEventsGoogleAnalytics(
                'Button click',
                'Alloy wheel button clicks',
                'Clicked continue without selecting a wheel'
            );
        }
    };

    const getWheelListItem = (wheel: string) => {
        return (
            <li
                key={wheel}
                className='list-group-item d-flex justify-content-between align-items-start'>
                {TranslationHelper.getTranslatedText(wheel)}
                <span className='badge rounded-pill' style={{ color: 'green' }}>
                    <FontAwesomeIcon icon={faCheck} size='2x' />
                </span>
            </li>
        );
    };

    const getClickedWheels = () => {
        return (
            <ul className='list-group list-group-flush'>
                {selectedWheels.map((wheel) => getWheelListItem(wheel))}
            </ul>
        );
    };

    const onWheelClick = (wheel: string) => {
        setShowModal(true);
        setModalTitle(TranslationHelper.getTranslatedText(wheel));
        onWheelSelected(wheel);
    };

    const forCancelButton = () => {
        const buttonText =
            selectedWheels.length > 1
                ? t('button_label.cancel_repair_requests')
                : t('button_label.cancel_repair_request');
        return (
            <button
                data-testid='danger-button'
                className='btn btn-danger progress-button'
                onClick={onCancelClick}>
                {buttonText}
            </button>
        );
    };
    const cancelButton = forCancelButton();

    return (
        <>
            <div className='card page-content'>
                <div className='card-body page-content-body'>
                    <FormInput
                        id='wheel-selection-forminput'
                        label={t(
                            'alloy_repair_request.step_2_wheel_selection_title'
                        )}
                        errorText={formErrors}>
                        <CarComponent
                            isLeftHandDrive={isAllowedInLocation(
                                [Countries.Us, Countries.De, Countries.At],
                                siteLocation
                            )}
                            selectedWheels={selectedWheels}
                            onWheelSelected={onWheelClick}
                        />
                    </FormInput>
                </div>
                <div className='card-footer page-content-footer'>
                    {CommonValidation.isNotEmpty(formErrors) && (
                        <ErrorSummary
                            title={t('wheel_selection_errors')}
                            errors={[formErrors]}
                            className='card-error'
                        />
                    )}
                </div>
                {getClickedWheels()}
            </div>
            <div className='bottom-navigation'>
                <button
                    data-testid='primary-button'
                    className='btn btn-primary progress-button'
                    onClick={onNextButtonClick}>
                    {t('button_label.continue')}
                </button>
                <button
                    data-testid='secondary-button'
                    className='btn btn-secondary progress-button'
                    onClick={() => onChangeStage(AlloyProcessStages.General)}>
                    {t('button_label.back')}
                </button>
                {cancelButton}
            </div>
            <ConfirmAlert
                showAlert={showCancelAlert}
                onConfirm={onCancelRepairRequest}
                onCancel={onCancelUndo}
                alertText={cancelConfirmText}
            />
            <Modal isOpen={showDialog} className='alloy-wheel-selection'>
                <ModalHeader toggle={onCancel}>{modalTitle}</ModalHeader>
                <ModalBody className='alloy-wheel-form'>
                    {showDialog && (
                        <AlloySpecificForm
                            repairRequest={repairRequest}
                            formErrors={wheelFormErrors}
                            onChange={onWheelValueChange}
                            onChangeDamageType={onChangeDamageType}
                            onUploadImageChange={onUploadImageChange}
                            errorSummaryTitle={`${modalTitle} Errors`}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        className='btn btn-primary progress-button'
                        color='primary'
                        onClick={onSave}>
                        {t('button_label.confirm')}
                    </Button>
                    <Button
                        className='btn btn-danger progress-button'
                        onClick={onShowConfirmation}>
                        {t('button_label.delete')}
                    </Button>
                    <Button
                        className='btn btn-secondary progress-button'
                        onClick={onCancel}>
                        {t('button_label.cancel')}
                    </Button>
                </ModalFooter>
            </Modal>
            <ModalDialog
                showDialog={showConfirmation}
                title={t('confirm_delete')}
                primaryButton={{
                    text: t('confirm'),
                    disabled: false,
                    onClick: onDeleteWheel
                }}
                secondaryButton={{
                    text: t('cancel'),
                    onClick: onCloseConfirmation
                }}
                onCloseClick={onCloseConfirmation}>
                {t('delete_alloy_wheel_request')}
            </ModalDialog>
        </>
    );
};

export default AlloyWheelSelection;
